import { Box } from '../box/box';
import { FieldContainer } from '../field-container/field-container';
import type { InputFieldProps } from '../input-field/input-field';
import { InputField } from '../input-field/input-field';

import * as styles from './date-input.css';

export interface DateInputProps
  extends Pick<
    InputFieldProps,
    'size' | 'label' | 'name' | 'disabled' | 'readOnly' | 'showField'
  > {
  id: string;
  dayInputProps: InputFieldProps & { dayKey?: string };
  monthInputProps: InputFieldProps & { monthKey?: string };
  yearInputProps: InputFieldProps & { yearKey?: string };
}

export const DateInput = ({
  disabled,
  readOnly,
  label,
  name,
  id,
  showField = true,
  size,
  dayInputProps: {
    dayKey,
    label: dayLabel,
    helpText: dayHelpText,
    placeholder: dayPlaceholder,
    errors: dayErrors,
    'aria-describedby': dayErrorMessageId,
    'aria-invalid': dayInvalid,
    ...dayInputProps
  },
  monthInputProps: {
    monthKey,
    label: monthLabel,
    helpText: monthHelpText,
    placeholder: monthPlaceholder,
    errors: monthErrors,
    'aria-describedby': monthErrorMessageId,
    'aria-invalid': monthInvalid,
    ...monthInputProps
  },
  yearInputProps: {
    yearKey,
    label: yearLabel,
    helpText: yearHelpText,
    placeholder: yearPlaceholder,
    errors: yearErrors,
    'aria-describedby': yearErrorMessageId,
    'aria-invalid': yearInvalid,
    ...yearInputProps
  },
}: DateInputProps) => {
  return showField ? (
    <FieldContainer
      variant="fieldGroup"
      label={label}
      name={name}
      htmlFor={id}
      disabled={disabled}
      readOnly={readOnly}
      errors={[
        ...(dayErrors || []),
        ...(monthErrors || []),
        ...(yearErrors || []),
      ]}
      errorMessageId={[
        dayErrorMessageId,
        monthErrorMessageId,
        yearErrorMessageId,
      ].join(' ')}
      aria-invalid={dayInvalid || monthInvalid || yearInvalid}
    >
      <Box variant="grid" spacing="base" className={styles.fields}>
        <InputField
          key={dayKey}
          aria-describedby={dayErrorMessageId}
          aria-invalid={dayInvalid}
          disabled={disabled}
          readOnly={readOnly}
          helpText={dayHelpText}
          label={dayLabel}
          placeholder={dayPlaceholder}
          size={size}
          {...dayInputProps}
        />

        <InputField
          key={monthKey}
          aria-describedby={monthErrorMessageId}
          aria-invalid={monthInvalid}
          disabled={disabled}
          readOnly={readOnly}
          helpText={monthHelpText}
          label={monthLabel}
          placeholder={monthPlaceholder}
          size={size}
          {...monthInputProps}
        />

        <InputField
          key={yearKey}
          aria-describedby={yearErrorMessageId}
          aria-invalid={yearInvalid}
          disabled={disabled}
          readOnly={readOnly}
          helpText={yearHelpText}
          label={yearLabel}
          placeholder={yearPlaceholder}
          size={size}
          {...yearInputProps}
        />
      </Box>
    </FieldContainer>
  ) : null;
};
